import React, { Component } from 'react';
import ReactTable from 'react-table';
import withFixedColumns from 'react-table-hoc-fixed-columns';
import cx from 'classnames';
import { filter, includes, intersection, pullAll, uniq } from 'lodash';
import { comparePrePropsAndNextProps } from '../../../lib/common';
import CheckBox from '../../input/CheckBox';
import { NoDataComponent } from '../NoDataComponent';
import './Table.scss';

/**
 *  컬럼을 고정시킬수 있는 HOC 추가
 *  사용법 : https://www.npmjs.com/package/react-table-hoc-fixed-columns
 */
const ReactTableFixedColumns = withFixedColumns(ReactTable);

/**
 *  checkbox 를 사용하는 테이블인 경우 컬럼리스트에 checkbox 컬럼을 추가함
 */
const checkBoxColumn = (data) => {
  const { isAllCheck, isEditable = true, onCheck, checkedIdList, disabledCheckedIdList, isUsedAllCheck } = data;

  return {
    Header: () =>
      isUsedAllCheck ? (
        <CheckBox
          checked={isAllCheck}
          disabled={!isEditable}
          onChange={(checked) =>
            onCheck({
              checked,
              id: 'all',
            })
          }
        />
      ) : null,
    resizable: false,
    filterable: false,
    fixed: 'left',
    width: 45,
    Cell: (row) => (
      <CheckBox
        checked={!!includes(checkedIdList, row.original.id)}
        disabled={
          !isEditable ||
          (!!disabledCheckedIdList &&
            disabledCheckedIdList.length > 0 &&
            !!includes(disabledCheckedIdList, row.original.id))
        }
        onChange={(checked) =>
          onCheck({
            checked,
            id: row.original.id,
          })
        }
      />
    ),
  };
};

class Table extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isAllCheck: false,
      checkedIdList: [],
    };
  }

  /**
   *  checkbox 를 사용하는 경우,
   *  체크된 항목 리스트와 전체 체크 여부를 확인함
   */
  static getDerivedStateFromProps(props, state) {
    if (
      props.checkedIdList &&
      comparePrePropsAndNextProps([
        [props.checkedIdList, state.checkedIdList],
        [props.data, state.data],
      ])
    ) {
      const keyList = props.data.map((item) => item[props.checkKey]);
      const matchingIdList = intersection(keyList, props.checkedIdList);
      return {
        isAllCheck: !!(keyList.length && keyList.length === matchingIdList.length),
        checkedIdList: props.checkedIdList,
      };
    }
    return null;
  }

  /**
   *  Table Component 의 변경되는 props 와 state 가 있을 경우에만,
   *  render 메소드를 호출할 수 있도록 함
   */
  shouldComponentUpdate = (nextProps, nextState) => {
    return comparePrePropsAndNextProps([
      [nextProps, this.props],
      [nextState, this.state],
    ]);
  };

  /**
   *  테이블 필터 callback
   */
  onFilter = (changed = {}) => {
    const { filterOption, pagination, getList } = this.props;
    getList({
      filterOption: {
        ...filterOption,
        ...changed,
      },
      pagination: {
        ...pagination,
        pageNum: 0,
      },
    });
  };

  /**
   *  페이지네이션 callback
   */
  onPagination = (changed) => {
    const { pagination, getList } = this.props;
    getList({
      pagination: {
        ...pagination,
        ...changed,
      },
    });
  };

  /**
   *  정렬 필터 callback
   */
  onSortedChange = (changed) => {
    const { getList } = this.props;
    getList({
      sortOption: changed,
    });
  };

  /**
   *  체크박스 callback
   */
  onCheck = ({ id, checked }) => {
    const { onCheck, data, checkKey, disabledCheckedIdList } = this.props;
    const { checkedIdList } = this.state;

    /**
     *  리스트가 있을 경우에만 onCheck callback 이 작동 되도록 한다.
     */
    if (!data.length) return;

    let targetList = [];

    /**
     *  일괄 checkBox
     */
    if (id === 'all') {
      /**
       *  리스트의 key 리스트
       */
      const dataKeyList = data.map((item) => item[checkKey]);
      /**
       *  체크
       */
      if (checked) targetList = uniq([...checkedIdList, ...dataKeyList]);
      /**
       *  체크 해제
       */ else {
        const filteredDataKeyList = [...pullAll(dataKeyList, disabledCheckedIdList)];
        targetList = [...pullAll(checkedIdList, filteredDataKeyList)];
      }
    } else {
      /**
       *  개별 checkBox
       */
      /**
       *  체크
       */
      if (checked) targetList = [...checkedIdList, id];
      /**
       *  체크 해제
       */ else targetList = filter(checkedIdList, (item) => item !== id);
    }

    onCheck(targetList);
  };

  /**
   *  컬럼 리스트
   *  컬럼 리스트를 만들때 search, isEditable, onFilter, checkBox 관련된 props 를 넘겨줌
   */
  setColumns = (columns) => {
    const {
      data = [],
      isUsedCheckbox,
      isEditable = true,
      filterOption,
      isUsedAllCheck = true,
      disabledCheckedIdList,
    } = this.props;
    const { isAllCheck, checkedIdList } = this.state;

    /**
     *  필터 메소드, 필터 검색 메소드 추가
     */
    columns = columns({
      list: data,
      isEditable,
      filterOption,
      onFilter: this.onFilter,
    });

    /**
     *  체크박스 사용 여부
     */
    if (isUsedCheckbox && isEditable) {
      columns.unshift(
        checkBoxColumn({
          onCheck: this.onCheck,
          isEditable,
          isUsedAllCheck,
          isAllCheck,
          checkedIdList,
          disabledCheckedIdList,
        }),
      );
    }

    return columns;
  };

  render() {
    const {
      className,
      isStriped = false,
      emptyText,
      data,
      minRows,
      showPagination = false,
      filterable = false,
      resizable = false,
      sortable = false,
      pagination,
      columns,
      showPaginationBottom = true,
      manual = true,
      sortOption,
      pageSizeOptions = [100, 150, 200, 500],
      defaultPageSize = 100,
      maxHeight = 800,
      ...res
    } = this.props;

    return (
      <ReactTableFixedColumns
        noDataText={emptyText}
        NoDataComponent={NoDataComponent}
        showPagination={showPagination}
        minRows={minRows ? data.length : 0}
        style={{
          maxHeight,
        }}
        data={data}
        manual={manual}
        previousText="이전"
        nextText="다음"
        ofText="/"
        pageText="페이지"
        rowsText=""
        pageSizeOptions={pageSizeOptions}
        defaultPageSize={defaultPageSize}
        showPaginationBottom={showPagination && showPaginationBottom}
        filterable={filterable}
        resizable={resizable}
        sortable={sortable}
        onPageChange={(pageNum) => manual && this.onPagination({ pageNum })}
        onPageSizeChange={(pageSize) => manual && this.onPagination({ pageSize, pageNum: 0 })}
        onSortedChange={(newSorted) => manual && this.onSortedChange([...newSorted])}
        page={manual ? pagination.pageNum : undefined}
        pages={manual ? pagination.pages : undefined}
        pageSize={manual ? pagination.pageSize : undefined}
        defaultSorted={sortOption}
        columns={this.setColumns(columns)}
        {...res}
        className={cx({ '-striped -highlight': isStriped }, className, { isEmpty: !data.length })}
      />
    );
  }
}

Table.defaultProps = {
  checkKey: 'id',
  pagination: {
    pageSize: 100,
    pageNum: 0,
    pages: -1,
  },
};

export default Table;
